import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, CheckCircle2, XCircle, Clock, Receipt as ReceiptIcon,
  Image as ImageIcon, X, ChevronDown, Check, Ban, Link2, Copy,
  MessageCircle, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tab: list & approve/reject payment requests sent by clients.
 */
export const AdminPaymentRequestsSection = ({ authHeader }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all
  const [loading, setLoading] = useState(true);
  const [proofModal, setProofModal] = useState(null); // request currently being shown big
  const [actionRequest, setActionRequest] = useState(null); // request being approved
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [bookingLinkFor, setBookingLinkFor] = useState(null); // request whose booking link modal is open

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payment-requests`, {
        headers: authHeader(),
        params: filter === "all" ? {} : { status: filter },
      });
      setRequests(r.data || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useRealtimeEvents({
    admin_refresh: (d) => {
      if (d?.scope === "payment_requests") load();
    },
  });

  const proofUrl = (r) => `${API}/admin/payment-requests/${r.id}/proof`;

  return (
    <div className="space-y-3" data-testid="admin-payment-requests">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-serif text-lg text-white flex items-center gap-2">
          <ReceiptIcon className="w-4 h-4 text-[#d4af37]" />
          Solicitudes de pago
        </h3>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["pending", "approved", "rejected", "all"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`apr-filter-${k}`}
              className={`text-[11px] px-3 py-1.5 rounded-full uppercase tracking-widest ${
                filter === k
                  ? "bg-[#d4af37] text-black font-bold"
                  : "bg-black/40 border border-white/10 text-neutral-400 hover:text-white"
              }`}
            >
              {k === "pending" ? "Pendientes" : k === "approved" ? "Aprobadas" : k === "rejected" ? "Rechazadas" : "Todas"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#d4af37] mx-auto" /></div>
      ) : requests.length === 0 ? (
        <div className="glass-dark rounded-xl p-6 text-center text-neutral-500 italic text-sm">
          No hay solicitudes {filter === "pending" ? "pendientes" : filter === "all" ? "" : filter}.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {requests.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              authHeader={authHeader}
              onViewProof={() => setProofModal(r)}
              onApprove={() => setActionRequest(r)}
              onReject={() => setRejectingRequest(r)}
              onBookingLink={() => setBookingLinkFor(r)}
            />
          ))}
        </div>
      )}

      {/* Booking-link modal */}
      {bookingLinkFor && (
        <BookingLinkModal
          request={bookingLinkFor}
          authHeader={authHeader}
          onClose={() => setBookingLinkFor(null)}
        />
      )}

      {/* Proof modal */}
      {proofModal && (
        <ProofViewer
          src={proofUrl(proofModal)}
          token={authHeader().Authorization}
          onClose={() => setProofModal(null)}
        />
      )}

      {/* Approve dialog */}
      {actionRequest && (
        <ApproveDialog
          request={actionRequest}
          authHeader={authHeader}
          onClose={(refreshed) => { setActionRequest(null); if (refreshed) load(); }}
        />
      )}

      {/* Reject dialog */}
      {rejectingRequest && (
        <RejectDialog
          request={rejectingRequest}
          authHeader={authHeader}
          onClose={(refreshed) => { setRejectingRequest(null); if (refreshed) load(); }}
        />
      )}
    </div>
  );
};


const statusMeta = {
  pending: { label: "Pendiente", icon: Clock, cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  approved: { label: "Aprobado", icon: CheckCircle2, cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  rejected: { label: "Rechazado", icon: XCircle, cls: "border-red-500/40 text-red-300 bg-red-500/10" },
};


const RequestCard = ({ request, authHeader, onViewProof, onApprove, onReject, onBookingLink }) => {
  const meta = statusMeta[request.status] || statusMeta.pending;
  const Icon = meta.icon;
  // Inline thumb of the proof image (one quick GET; cached by browser)
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!request.has_proof) return undefined;
    let cancelled = false;
    axios.get(`${API}/admin/payment-requests/${request.id}/proof`, {
      headers: authHeader(),
      responseType: "blob",
    }).then((r) => {
      if (cancelled) return;
      const url = URL.createObjectURL(r.data);
      setThumb(url);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [request.id, request.has_proof, authHeader]);

  return (
    <div className="glass-dark rounded-xl p-4" data-testid={`admin-pr-card-${request.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-white text-sm font-semibold">{request.user_name}</div>
          <div className="text-xs text-neutral-400">{request.service_label}</div>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${meta.cls}`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
        <div className="bg-black/40 border border-white/5 rounded px-2 py-1.5">
          <span className="text-neutral-500">Tipo: </span>
          <span className="text-white">{request.payment_type === "completo" ? "Pago completo" : "Abono parcial"}</span>
        </div>
        <div className="bg-black/40 border border-white/5 rounded px-2 py-1.5">
          <span className="text-neutral-500">Monto: </span>
          <span className="text-emerald-300 font-bold">${Number(request.amount).toLocaleString("es-MX")} {request.currency}</span>
        </div>
      </div>

      {request.payment_type === "abono" && request.agreed_price && (
        <div className="text-[11px] text-neutral-400 mb-2">
          Total acordado del servicio: <b>${Number(request.agreed_price).toLocaleString("es-MX")}</b>
        </div>
      )}

      {request.notes && (
        <div className="text-[11px] italic text-neutral-400 mb-2">📝 {request.notes}</div>
      )}

      {request.has_proof && (
        <button
          data-testid={`apr-view-proof-${request.id}`}
          onClick={onViewProof}
          className="w-full mb-2 rounded-lg overflow-hidden border border-[#d4af37]/30 hover:border-[#d4af37] transition-colors block bg-black/30"
        >
          {thumb ? (
            <img src={thumb} alt="comprobante" className="w-full max-h-44 object-contain" />
          ) : (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-neutral-400">
              <ImageIcon className="w-4 h-4" /> Cargando comprobante...
            </div>
          )}
        </button>
      )}

      {request.status === "rejected" && request.rejection_motive && (
        <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/20 rounded p-2 mb-2">
          <b>Motivo rechazo:</b> {request.rejection_motive}
        </div>
      )}

      <div className="text-[10px] text-neutral-500 mb-2">
        {new Date(request.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
      </div>

      {request.status === "pending" && (
        <div className="flex gap-2">
          <button
            data-testid={`apr-approve-${request.id}`}
            onClick={onApprove}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5"
          >
            <Check className="w-3.5 h-3.5" /> Aprobar
          </button>
          <button
            data-testid={`apr-reject-${request.id}`}
            onClick={onReject}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5"
          >
            <Ban className="w-3.5 h-3.5" /> Rechazar
          </button>
        </div>
      )}

      {request.status === "approved" && (
        <div className="mt-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1.5 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Pago aprobado — envíale este link al cliente
          </div>
          <button
            data-testid={`apr-booking-link-${request.id}`}
            onClick={onBookingLink}
            className="w-full bg-gradient-to-r from-[#d4af37] via-[#f3d878] to-[#d4af37] hover:brightness-110 text-black py-3 rounded-lg text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(212,175,55,0.3)] animate-pulse-slow"
          >
            <Link2 className="w-4 h-4" />
            Generar enlace para agendar cita
          </button>
          <div className="text-[10px] text-neutral-500 mt-1.5 text-center">
            El cliente lo abrirá sin necesidad de iniciar sesión
          </div>
        </div>
      )}
    </div>
  );
};


const ProofViewer = ({ src, token, onClose }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    axios.get(src, {
      headers: { Authorization: token },
      responseType: "blob",
    }).then((r) => setUrl(URL.createObjectURL(r.data))).catch(() => null);
  }, [src, token]);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[130] bg-black/90 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <button className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/60 hover:bg-black/80">
          <X className="w-5 h-5" />
        </button>
        {url ? (
          <img src={url} alt="comprobante" className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
        ) : (
          <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
        )}
      </motion.div>
    </AnimatePresence>
  );
};


const ApproveDialog = ({ request, authHeader, onClose }) => {
  const [amount, setAmount] = useState(request.amount?.toString() || "");
  const [paymentType, setPaymentType] = useState(request.payment_type || "abono");
  const [method, setMethod] = useState("Transferencia");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [postApproveLink, setPostApproveLink] = useState(null); // linkData when fully paid

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Monto inválido");
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/payment-requests/${request.id}/approve`,
        { amount: Number(amount), payment_type: paymentType, method, notes },
        { headers: authHeader() }
      );
      const fp = r.data?.fully_paid;
      toast.success(fp ? "Aprobado y SERVICIO LIQUIDADO. Recibo enviado al cliente." : "Aprobado. Abono registrado y recibo enviado.");
      // ALWAYS auto-generate the booking link so the admin can share it with
      // the client on the spot — regardless of whether the payment is
      // fully paid or just a partial (abono). The client needs to schedule
      // their session either way.
      try {
        const l = await axios.post(
          `${API}/admin/payment-requests/${request.id}/booking-link`,
          {},
          { headers: authHeader() },
        );
        setPostApproveLink(l.data);
        setSubmitting(false);
        return; // keep the modal open showing the link
      } catch {
        /* fall through — admin can generate the link manually from the row */
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = () => {
    if (!postApproveLink?.url) return;
    navigator.clipboard.writeText(postApproveLink.url)
      .then(() => toast.success("Enlace copiado"))
      .catch(() => toast.error("No se pudo copiar"));
  };

  const waHref = () => {
    if (!postApproveLink?.url) return "#";
    const phone = (request.user_phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(
      `Hola ${postApproveLink.user.name || postApproveLink.user.username}, aquí puedes agendar tu ${postApproveLink.service.name}: ${postApproveLink.url} El enlace es único y personal para ti. Elige el día y la hora que mejor te acomoden. Bendiciones.`
    );
    return phone
      ? `https://wa.me/${phone}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-md flex items-center justify-center px-3"
        data-testid="apr-approve-dialog"
      >
        <motion.div
          initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border border-emerald-500/30 rounded-2xl p-5"
        >
          <button onClick={() => onClose(true)} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
          <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
            {postApproveLink ? (
              <><Link2 className="w-5 h-5 text-[#d4af37]" /> Enlace de agendado</>
            ) : (
              <><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Aprobar pago</>
            )}
          </h3>
          <p className="text-xs text-neutral-400 mb-4">
            Cliente <b className="text-white">{request.user_name}</b> · {request.service_label}
          </p>

          {postApproveLink ? (
            <>
              <p className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 mb-3">
                ✓ Pago LIQUIDADO. Envíale este enlace al cliente para que agende su cita:
              </p>
              <div className="rounded-xl border border-[#d4af37]/30 bg-black/50 px-3 py-3 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">URL única</div>
                <div className="text-[11px] text-white font-mono break-all select-all" data-testid="post-approve-link-url">
                  {postApproveLink.url}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  data-testid="post-approve-copy"
                  onClick={copyUrl}
                  className="btn-outline-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
                <a
                  data-testid="post-approve-whatsapp"
                  href={waHref()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
                </a>
              </div>
              <button
                onClick={() => onClose(true)}
                className="w-full text-[11px] text-neutral-500 hover:text-neutral-300 py-2"
              >
                Cerrar
              </button>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button onClick={() => setPaymentType("abono")} className={`px-3 py-2 rounded text-xs uppercase tracking-widest border ${paymentType === "abono" ? "bg-[#d4af37] text-black border-[#d4af37] font-bold" : "bg-black/40 border-white/10 text-neutral-300"}`}>Abono</button>
                <button onClick={() => setPaymentType("completo")} className={`px-3 py-2 rounded text-xs uppercase tracking-widest border ${paymentType === "completo" ? "bg-[#d4af37] text-black border-[#d4af37] font-bold" : "bg-black/40 border-white/10 text-neutral-300"}`}>Liquidación</button>
              </div>
              <div className="mb-3">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Monto a registrar</Label>
                <Input type="number" data-testid="apr-approve-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 bg-black/60 border-white/10" />
              </div>
              <div className="mb-3">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Método</Label>
                <Input data-testid="apr-approve-method" value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1.5 bg-black/60 border-white/10" />
              </div>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Notas (recibo)</Label>
                <Textarea data-testid="apr-approve-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-black/60 border-white/10 text-sm" />
              </div>
              <button
                data-testid="apr-approve-confirm"
                onClick={submit} disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Aprobar y enviar recibo
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


const RejectDialog = ({ request, authHeader, onClose }) => {
  const [motive, setMotive] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!motive.trim()) return toast.error("Indica el motivo del rechazo");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/payment-requests/${request.id}/reject`,
        { motive },
        { headers: authHeader() }
      );
      toast.success("Solicitud rechazada. El cliente fue notificado.");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rechazar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-md flex items-center justify-center px-3"
        data-testid="apr-reject-dialog"
      >
        <motion.div
          initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border border-red-500/30 rounded-2xl p-5"
        >
          <button onClick={() => onClose(false)} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
          <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" /> Rechazar pago
          </h3>
          <p className="text-xs text-neutral-400 mb-4">
            Cliente <b className="text-white">{request.user_name}</b> · {request.service_label}
          </p>
          <Label className="text-xs uppercase tracking-widest text-red-300">Motivo del rechazo</Label>
          <Textarea
            data-testid="apr-reject-motive"
            value={motive}
            onChange={(e) => setMotive(e.target.value)}
            rows={3}
            placeholder="Ej: El comprobante no coincide con el monto, no se ve la fecha, etc."
            className="mt-1.5 bg-black/60 border-white/10 text-sm mb-4"
          />
          <button
            data-testid="apr-reject-confirm"
            onClick={submit} disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            Rechazar
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};



// ---------------------------------------------------------------------------
// BookingLinkModal — generates + displays the one-time booking link
// for an approved payment. Admin can copy the URL or share it via WhatsApp.
// ---------------------------------------------------------------------------
const BookingLinkModal = ({ request, authHeader, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.post(
      `${API}/admin/payment-requests/${request.id}/booking-link`,
      {},
      { headers: authHeader() },
    ).then((r) => {
      if (cancelled) return;
      setLinkData(r.data);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || "No se pudo generar el enlace");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request.id, authHeader]);

  const copyUrl = () => {
    if (!linkData?.url) return;
    navigator.clipboard.writeText(linkData.url)
      .then(() => toast.success("Enlace copiado"))
      .catch(() => toast.error("No se pudo copiar"));
  };

  const waLink = () => {
    if (!linkData?.url) return "#";
    const clientPhone = (request.user_phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(
      `Hola ${linkData.user.name || linkData.user.username}, aquí puedes agendar tu ${linkData.service.name}: ${linkData.url} El enlace es único y personal para ti. Elige el día y la hora que mejor te acomoden. Bendiciones.`
    );
    return clientPhone
      ? `https://wa.me/${clientPhone}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[140] bg-black/80 backdrop-blur-md flex items-center justify-center px-3"
        data-testid="apr-booking-link-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border border-[#d4af37]/40 rounded-2xl p-5"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>

          <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#d4af37]" /> Enlace de agendado
          </h3>
          <p className="text-xs text-neutral-400 mb-4">
            Envíale este enlace a <b className="text-white">{request.user_name}</b> por WhatsApp o
            copiándolo. Es único, personal y solo funciona para ese cliente.
          </p>

          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" />
            </div>
          ) : error ? (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              {error}
            </div>
          ) : linkData ? (
            <>
              <div className="rounded-xl border border-[#d4af37]/30 bg-black/50 px-3 py-3 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                  URL {linkData.reused ? "(reutilizada — aún válida)" : "(nueva)"}
                </div>
                <div className="text-[11px] text-white font-mono break-all select-all" data-testid="booking-link-url">
                  {linkData.url}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  data-testid="booking-link-copy"
                  onClick={copyUrl}
                  className="btn-outline-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
                <a
                  data-testid="booking-link-whatsapp"
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
                </a>
              </div>

              <div className="text-[10px] text-neutral-500 border-t border-white/5 pt-2">
                Expira: {linkData.expires_at ? new Date(linkData.expires_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
              </div>
            </>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
